import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ALIGNMENTS } from "@/data/enhancements";
import { PLATES } from "@/data/plates";
import AnomalyPlate from "@/components/AnomalyPlate";
import FieldNotes from "@/components/FieldNotes";
import CommunityTheories from "@/components/CommunityTheories";
import { useVotes, VoteRow } from "@/components/HypothesisVote";

const EASE = [0.22, 1, 0.36, 1];

export default function SiteDrawer({ site, sites, onClose, onNav, inRoute, onToggleRoute, inCompare, onToggleCompare }) {
  const idx = sites.findIndex((s) => s.id === site.id);
  const prev = sites[(idx - 1 + sites.length) % sites.length];
  const next = sites[(idx + 1) % sites.length];
  const [openPlate, setOpenPlate] = useState(0);
  const plates = PLATES[site.id] || [];
  const votes = useVotes(site.id);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <motion.div
        data-testid="drawer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={onClose}
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
      />
      <motion.aside
        data-testid="site-drawer"
        initial={{ x: "102%" }}
        animate={{ x: 0 }}
        exit={{ x: "102%" }}
        transition={{ duration: 0.55, ease: EASE }}
        className="fixed inset-y-0 right-0 z-[70] flex w-full flex-col border-l border-[#D4AF37]/25 bg-[#12141C] sm:w-[540px]"
      >
        <div className="drawer-scroll relative flex-1 overflow-y-auto" data-lenis-prevent>
          <div className="relative h-56 sm:h-64">
            <img
              src={site.image}
              alt={site.name}
              data-testid="drawer-image"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12141C] via-[#12141C]/25 to-transparent" />
            <span className="absolute left-5 top-5 border border-[#D4AF37]/50 bg-[#0B0C10]/70 px-3 py-1.5 font-mono text-[9px] tracking-[0.25em] text-[#D4AF37] backdrop-blur">
              {site.category.toUpperCase()}
            </span>
            <button
              data-testid="drawer-close"
              onClick={onClose}
              aria-label="Close panel"
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-[#0B0C10]/70 font-serif text-lg text-white/85 backdrop-blur transition-all duration-300 hover:rotate-90 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              ×
            </button>
          </div>

          <div className="px-6 pb-10 pt-2 sm:px-9">
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#38BDF8]">
              {site.location.toUpperCase()}
            </p>
            <h3 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
              {site.name}
            </h3>
            <p className="mt-3 font-mono text-[10px] tracking-[0.18em] text-white/45">
              {site.lat.toFixed(4)}° · {site.lng.toFixed(4)}° — {site.age}
            </p>

            {ALIGNMENTS[site.id] && (
              <p
                data-testid="drawer-alignment"
                className="mt-3 inline-block border border-[#38BDF8]/40 bg-[#38BDF8]/10 px-3 py-1.5 font-mono text-[9px] tracking-[0.3em] text-[#38BDF8]"
              >
                SKY ALIGNMENT — {ALIGNMENTS[site.id].name}
              </p>
            )}

            <div className="mt-6 flex gap-2">
              <button
                data-testid="drawer-route-button"
                onClick={() => onToggleRoute(site.id)}
                className={`flex-1 border py-3 font-mono text-[10px] tracking-[0.28em] transition-all duration-300 ${
                  inRoute
                    ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                    : "border-white/20 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
              >
                {inRoute ? "★ IN ROUTE — REMOVE" : "+ ADD TO ROUTE"}
              </button>
              <button
                data-testid="drawer-compare-button"
                onClick={() => onToggleCompare(site.id)}
                className={`border px-4 py-3 font-mono text-[10px] tracking-[0.28em] transition-all duration-300 ${
                  inCompare
                    ? "border-[#38BDF8] bg-[#38BDF8]/15 text-[#38BDF8]"
                    : "border-white/20 text-white/60 hover:border-[#38BDF8] hover:text-[#38BDF8]"
                }`}
              >
                {inCompare ? "⇄ PINNED" : "⇄ COMPARE"}
              </button>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.6, ease: EASE }}
              className="mt-8"
            >
              <p
                data-testid="drawer-mainstream-label"
                className="font-mono text-[10px] tracking-[0.35em] text-[#38BDF8]"
              >
                MAINSTREAM CONSENSUS
              </p>
              <p className="mt-3 text-sm font-light leading-relaxed text-white/70">
                {site.mainstream}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6, ease: EASE }}
              className="mt-9"
            >
              <p
                data-testid="drawer-hypotheses-label"
                className="font-mono text-[10px] tracking-[0.35em] text-[#D4AF37]"
              >
                ALTERNATIVE HYPOTHESES
              </p>
              <p data-testid="drawer-vote-total" className="mt-1.5 font-mono text-[8px] tracking-[0.25em] text-white/35">
                {votes.err
                  ? "CROWD TALLY UNAVAILABLE"
                  : votes.tally
                  ? `${votes.tally.total} VISITOR${votes.tally.total === 1 ? " HAS" : "S HAVE"} PICKED A FAVOURITE`
                  : "LOADING CROWD TALLY…"}
              </p>
              <div className="mt-4 space-y-3">
                {site.hypotheses.map((h, i) => {
                  const code = `H.${String(i + 1).padStart(2, "0")}`;
                  const expanded = openPlate === i && plates[i];
                  return (
                    <div
                      key={h.title}
                      data-testid={`drawer-hypothesis-${i}`}
                      className={`border bg-[#181B26] p-4 transition-colors duration-300 ${
                        expanded ? "border-[#D4AF37]/50" : "border-[#D4AF37]/20 hover:border-[#D4AF37]/50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-mono text-[9px] tracking-[0.3em] text-[#FF9F1C]">{code}</p>
                        {plates[i] && (
                          <button
                            data-testid={`drawer-plate-toggle-${i}`}
                            onClick={() => setOpenPlate(expanded ? -1 : i)}
                            aria-expanded={!!expanded}
                            className={`font-mono text-[8px] tracking-[0.25em] transition-colors ${
                              expanded ? "text-[#D4AF37]" : "text-white/40 hover:text-[#D4AF37]"
                            }`}
                          >
                            {expanded ? "− HIDE PLATE" : "+ ANOMALY PLATE"}
                          </button>
                        )}
                      </div>
                      <p className="mt-2 font-serif text-lg leading-snug text-[#F8FAFC]">{h.title}</p>
                      <p className="mt-2 text-[13px] font-light leading-relaxed text-white/60">{h.body}</p>
                      <AnimatePresence initial={false}>
                        {expanded && (
                          <AnomalyPlate
                            key="plate"
                            plate={plates[i]}
                            fallbackImage={site.image}
                            code={code}
                            testid={`drawer-plate-${i}`}
                          />
                        )}
                      </AnimatePresence>
                      <VoteRow i={i} votes={votes} />
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.6, ease: EASE }}
              className="mt-9"
            >
              <p className="font-mono text-[10px] tracking-[0.35em] text-[#38BDF8]">
                KEY ANOMALIES
              </p>
              <ul className="mt-4 space-y-2.5">
                {site.anomalies.map((a) => (
                  <li
                    key={a}
                    className="flex items-baseline gap-3 font-mono text-[11px] leading-relaxed text-white/55"
                  >
                    <span className="shrink-0 text-[#FF9F1C]">◦</span>
                    {a}
                  </li>
                ))}
              </ul>
            </motion.div>

            <CommunityTheories siteId={site.id} />

            <FieldNotes siteId={site.id} />

            <p className="mt-10 border-t border-white/10 pt-5 font-mono text-[9px] leading-relaxed tracking-[0.12em] text-white/30">
              ALTERNATIVE HYPOTHESES ARE PRESENTED AS CLAIMS, NOT ESTABLISHED
              FACT. THE MAINSTREAM READING IS ALWAYS SHOWN FIRST.
            </p>
          </div>
        </div>

        <div className="flex shrink-0 border-t border-[#D4AF37]/20">
          <button
            data-testid="drawer-prev"
            onClick={() => onNav(prev)}
            className="group flex-1 px-5 py-4 text-left transition-colors hover:bg-[#D4AF37]/10"
          >
            <span className="block font-mono text-[8px] tracking-[0.3em] text-white/40">
              ← PREV
            </span>
            <span className="mt-1 block truncate font-serif text-sm text-white/75 group-hover:text-[#D4AF37]">
              {prev.name}
            </span>
          </button>
          <div className="w-px bg-[#D4AF37]/20" />
          <button
            data-testid="drawer-next"
            onClick={() => onNav(next)}
            className="group flex-1 px-5 py-4 text-right transition-colors hover:bg-[#D4AF37]/10"
          >
            <span className="block font-mono text-[8px] tracking-[0.3em] text-white/40">
              NEXT →
            </span>
            <span className="mt-1 block truncate font-serif text-sm text-white/75 group-hover:text-[#D4AF37]">
              {next.name}
            </span>
          </button>
        </div>
      </motion.aside>
    </>
  );
}
